import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts/notification-context"
import { Address } from "@/models/address"
import { ApiQueryName } from "@/models/api"
import type { Site } from "@/models/site"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { useInvalidateQueries } from "@/services/utils/useInvalidateQueries"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

export interface SiteUpdate {
  name: Site["name"]
  buildingType: NonNullable<Site["buildingType"]>
  sqFt: NonNullable<Site["sqFt"]>
  address: {
    address_line_1: string
    address_line_2: string
    city: string
    country: string
    postal_code: string
    state: string
  }
}

export interface UpdateSiteInput {
  site: Site
  updates: SiteUpdate
}

const applyUpdates = (site: Site, updates: SiteUpdate): void => {
  site.name = updates.name
  site.buildingType = updates.buildingType || null
  site.sqFt = updates.sqFt || null
  const hasAddressInput = Object.values(updates.address).some(
    (value) => value.trim().length > 0
  )

  if (!hasAddressInput) {
    return
  }

  if (!site.address) {
    site.address = new Address({
      ...updates.address,
    })
    return
  }

  site.address.address_line_1 = updates.address.address_line_1
  site.address.address_line_2 = updates.address.address_line_2
  site.address.city = updates.address.city
  site.address.state = updates.address.state
  site.address.postal_code = updates.address.postal_code
  site.address.country = updates.address.country
}

export const useUpdateSite = (
  options?: UseMutationOptions<boolean, Error, UpdateSiteInput>
) => {
  const { invalidateQueries } = useInvalidateQueries()
  const { setNotification } = useNotification()
  const { t } = useTranslation("common")

  const mutation = useMutation({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn: async ({ site, updates }: UpdateSiteInput) => {
      applyUpdates(site, updates)
      return await site.save({ with: ["address"] })
    },
    onError: (error, ...rest) => {
      setNotification({
        message: getErrorNotificationMessage(
          error,
          t("editSite.updateSiteError")
        ),
        type: "error",
      })

      options?.onError?.(error, ...rest)
    },
    onSuccess: (data, ...rest) => {
      // Necessary because Spraypaint returns false on 422 errors
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
      if (data === false) {
        setNotification({
          message: t("editSite.updateSiteError"),
          type: "error",
        })
        return
      }

      invalidateQueries([
        createQueryKey(ApiQueryName.Site, "getOne"),
        createQueryKey(ApiQueryName.Sites, "getMany"),
        createQueryKey(ApiQueryName.SiteExplorer, "getMany"),
        createQueryKey(ApiQueryName.Meters, "getMany"),
      ])

      options?.onSuccess?.(data, ...rest)
    },
  })

  return useRenameQueryResult(mutation, "updateSite")
}
